import { css } from '@emotion/react'
import { FC, PropsWithChildren, ReactElement, ReactNode } from 'react'
import { SItem, SItemView, STabItems, STitle, STitleActionRow } from './Gallery.styled'
import { pxToRem } from 'utils/styled'
import { RouterTabs } from 'components/tabs/RouterTabs'
import { Caption } from 'components/Typography'
import { formatDate } from 'utils/date'
import { parseISO } from 'date-fns'

const DEFAULT_ITEM_SIZE = 200

export type GalleryItem = {
  id: string
  view?: ReactElement
  created?: string // Raw ISO timestamp, formatted at display time
  width?: number | null
  height?: number | null
}

export type GalleryTab = {
  label: ReactNode
  content?: GalleryItem[]
  customContent?: ReactNode
  placeholder?: ReactNode
  footer?: ReactElement
  key: string
  action?: ReactNode
}

export type GalleryProps = {
  className?: string
  onTabSelected: (value: string) => void
  tabs?: GalleryTab[]
  placeholder?: ReactNode
  rowHeight?: number
  hideTabs?: boolean
  defaultTab?: GalleryTab
  noShowPlaceholder?: boolean
  tabContentBasis?: string
}

export const Gallery: FC<PropsWithChildren<GalleryProps>> = (props) => {
  const {
    className,
    onTabSelected,
    tabs,
    placeholder,
    rowHeight = DEFAULT_ITEM_SIZE,
    hideTabs,
    noShowPlaceholder,
  } = props

  const isEmpty = tabs?.every((t) => !t.content?.length)

  if (isEmpty && !noShowPlaceholder) return <>{placeholder}</>

  return (
    <RouterTabs
      className={className}
      onTabSelected={onTabSelected}
      hideTabs={hideTabs}
      defaultTabKey={props.defaultTab?.key}
      tabContentCss={
        props.tabContentBasis && {
          flexBasis: props.tabContentBasis,
        }
      }
      tabs={(tabs ?? []).map((tab, j) => {
        return {
          key: tab.key,
          label: tab.label,
          content: (
            <>
              <STitleActionRow>
                <Caption>{tab.label}</Caption>
                {tab.action}
              </STitleActionRow>
              {tab.customContent ? (
                tab.customContent
              ) : tab?.content?.length ? (
                <>
                  <STabItems>
                    {tab.content?.map((item, i) => {
                      const displayDate = item?.created
                        ? formatDate(parseISO(item.created), 'medium')
                        : ''

                      const itemWidth =
                        ((item.width || rowHeight) * rowHeight) / (item.height || rowHeight)
                      return (
                        <SItem
                          css={css`
                            height: calc(${rowHeight}px + ${pxToRem(40)}rem);
                            width: ${itemWidth}px;
                            flex-basis: ${itemWidth}px;
                            max-width: 100%;
                          `}
                          key={j.toFixed() + i.toFixed()}
                        >
                          <STitle>{displayDate}</STitle>
                          <SItemView css={{ height: `${rowHeight}px` }}>{item.view}</SItemView>
                        </SItem>
                      )
                    })}
                  </STabItems>
                  {tab.footer}
                </>
              ) : (
                tab?.placeholder || placeholder
              )}
            </>
          ),
        }
      })}
    />
  )
}
